import template from "./hb-passkey.mjs";
import { render } from "lit-html";
import { HeartBank } from "../../../managers/HeartBank";
import { Crypto } from "../../../managers/Crypto.ts";
import { HbMain } from "../../hb-main/hb-main.ts";
import { isInIframe, iframe, opener, wait, redirect } from "../../global.ts";

import { Dismiss } from "flowbite";
import type { DismissOptions, DismissInterface } from "flowbite";
import type { InstanceOptions } from "flowbite";

export class HbPasskey extends HTMLElement {
    private heartbank = new HeartBank();
    private crypto = new Crypto();
    private main = document.querySelector("hb-main") as HbMain;

    private timer = 0;
    private toast: DismissInterface;
    private passkey: HTMLElement;
    private info: HTMLElement;
    private close: HTMLButtonElement;

    constructor() {
        super();
        render(template(null), this);

        this.passkey = this.querySelector("#hb-passkey")!;
        this.info = this.querySelector("#hb-passkey-info")!;
        this.close = this.querySelector("#hb-passkey-close")!;

        const options: DismissOptions = {
            transition: "transition-opacity",
            duration: 1000,
            timing: "ease-out",
            onHide: (context, targetEl) => {
                //this.main.hideSpinner();
                if (!this.heartbank.user) {
                    if (window.location.hash === "#/passkey")
                        window.location.hash = "#/login";
                    else window.location.hash = "#";
                }
                //console.log(context, targetEl);
            }
        };

        const instanceOptions: InstanceOptions = {
            id: "hb-passkey",
            override: true
        };

        this.toast = new Dismiss(
            this.passkey,
            this.close,
            options,
            instanceOptions
        );
    }

    connectedCallback() {
        this.close.addEventListener("click", () => {
            window.clearTimeout(this.timer);
        });

        // this.heartbank.addAuthStateListener(async (user) => {
        //     render(template(user), this);

        //     // redundant with hb-login.ts
        //     // //this.main.showSpinner();
        //     // const fragments = window.location.hash.split("/");
        //     // if (fragments[1] === "passkey") {
        //     //     if (user) {
        //     //         if (this.heartbank.opener) {
        //     //             //this.main.showSpinner();
        //     //             const status = await this.heartbank.getCustomToken();
        //     //             this.heartbank.opener.postMessage(
        //     //                 {
        //     //                     action:
        //     //                         status.color === "green"
        //     //                             ? "success"
        //     //                             : "error",
        //     //                     text: status.text,
        //     //                     token: status.token
        //     //                 },
        //     //                 "*"
        //     //             );
        //     //         } else if (this.heartbank.isInIframe) {
        //     //             //this.main.showSpinner();
        //     //             const status = await this.heartbank.getCustomToken();
        //     //             this.heartbank.iframe.postMessage(
        //     //                 {
        //     //                     action:
        //     //                         status.color === "green"
        //     //                             ? "success"
        //     //                             : "error",
        //     //                     text: status.text,
        //     //                     token: status.token
        //     //                 },
        //     //                 "*"
        //     //             );
        //     //         } else {
        //     //             window.location.hash = "#/account";
        //     //         }
        //     //     } else {
        //     //         //render(template(user), this);
        //     //     }
        //     // }
        //     // //this.main.hideSpinner();
        // });
    }

    private show(info: string) {
        this.info.textContent = info;
        this.info.style.color = "orange";
        this.passkey.classList.remove("hidden", "opacity-100");
        this.passkey.classList.add("flex", "opacity-100");
    }

    private hide() {
        this.toast.hide();
        this.main.hideSpinner(); // needed bc weird bug when user cancels passkey
    }

    async login() {
        this.main.showSpinner();
        this.show("Initiating passkey authentication...");
        const status = await this.heartbank.loginWithPasskey();
        this.info.textContent = status.text;
        this.info.style.color = status.color;
        this.main.hideSpinner();
        this.timer = window.setTimeout(this.hide.bind(this), 1000);
        redirect("redirect-login");
    }

    async logout() {
        this.main.showSpinner();
        this.show("Logging out of HeartBank® Vault...");
        if (process.env.NODE_ENV !== "production") await wait(1);
        const status = await this.heartbank.signOut();
        this.info.textContent = status.text;
        this.info.style.color = status.color;

        if (opener) {
            opener.postMessage(
                {
                    action: "logout",
                    text: status.text
                },
                "*"
            );
        } else if (isInIframe) {
            iframe?.postMessage(
                {
                    action: "logout",
                    text: status.text
                },
                "*"
            );
        } else {
            this.main.hideSpinner();
            this.timer = window.setTimeout(this.hide.bind(this), 1000);
        }
    }

    async sign(content: string) {
        this.main.showSpinner();
        this.show("Signing your message...");
        const { text, color, signature } = await this.crypto.sign(content);
        this.info.textContent = text;
        this.info.style.color = color;

        if (opener) {
            opener.postMessage(
                {
                    action: "sign",
                    text,
                    content, // remove?
                    signature
                },
                "*"
            );
        } else if (isInIframe) {
            iframe?.postMessage(
                {
                    action: "sign",
                    text,
                    content, // remove?
                    signature
                },
                "*"
            );
        } else {
            this.main.hideSpinner();
            this.timer = window.setTimeout(this.hide.bind(this), 1000);
        }
    }

    async encrypt(content: string) {
        this.main.showSpinner();
        this.show("Encryping your message...");
        const {
            text,
            color,
            kid,
            iv,
            encryptedSymmetricKey,
            encryptedContent,
            signature
        } = await this.crypto.encrypt(content);
        this.info.textContent = text;
        this.info.style.color = color;

        if (opener) {
            opener.postMessage(
                {
                    action: "encrypt",
                    text,
                    kid,
                    iv,
                    encryptedSymmetricKey,
                    encryptedContent,
                    signature
                },
                "*"
            );
        } else if (isInIframe) {
            iframe?.postMessage(
                {
                    action: "encrypt",
                    text,
                    kid,
                    iv,
                    encryptedSymmetricKey,
                    encryptedContent,
                    signature
                },
                "*"
            );
        } else {
            this.main.hideSpinner();
            this.timer = window.setTimeout(this.hide.bind(this), 1000);
        }
    }

    async decrypt(
        //uid: string TODO:
        kid: string,
        iv: string,
        encryptedSymmetricKey: string,
        encryptedContent: string,
        signature: string
    ) {
        this.main.showSpinner();
        this.show("Verifying the signature...");
        const isValid = await this.crypto.verify(
            kid,
            encryptedContent,
            signature
        );

        if (isValid) {
            this.show("Decryping your message...");
            const { text, color, decryptedContent } = await this.crypto.decrypt(
                kid,
                iv,
                encryptedSymmetricKey,
                encryptedContent
            );
            this.info.textContent = text;
            this.info.style.color = color;

            if (opener) {
                opener.postMessage(
                    {
                        action: "decrypt",
                        text,
                        decryptedContent
                    },
                    "*"
                );
            } else if (isInIframe) {
                iframe?.postMessage(
                    {
                        action: "decrypt",
                        text,
                        decryptedContent
                    },
                    "*"
                );
            } else {
                this.main.hideSpinner();
                this.timer = window.setTimeout(this.hide.bind(this), 1000);
            }
        } else {
            if (opener) {
                opener.postMessage(
                    {
                        action: "error",
                        text: "Signature verification failed."
                    },
                    "*"
                );
            } else if (isInIframe) {
                iframe?.postMessage(
                    {
                        action: "error",
                        text: "Signature verification failed."
                    },
                    "*"
                );
            } else {
                this.main.hideSpinner();
                this.timer = window.setTimeout(this.hide.bind(this), 1000);
            }
        }
    }
}
